import store from "../../store/store";
import Lead from "../../entities/lead";
import util from "../../lib/util";
import {uuid} from "../../@types/schema";
import RedFlag from "../../entities/redFlag";
import Matter from "../../entities/matter";
import RedFlagType from "../../entities/redFlagType";
import CaseHelper from "../../Case/CaseHelper";

/**
 * Get all red flags for this id.
 * The id could belong to a lead or case.
 *
 * @param {uuid} entityId
 * @returns {RedFlag[]}
 */
export function getRedFlags(entityId: uuid): RedFlag[] {
    return store.getState().redFlags.getMappingFor('entityId', entityId);
}

/**
 * Get all red flags for this red flag type.
 *
 * @param {Lead | Matter} entity
 * @returns {Array}
 */
export function getRedFlagTypes(entity: Lead | Matter): Array<RedFlagType> {
    // If no lead present, load all GENERAL red flags and caseType-specific red flags from RedFlagTypes
    const redFlagTypes = store.getState().redFlagTypes
        .getMappingFor('entityType', entity.entityType)
        .filter(q => {
            // No deleted red flag types
            if (q.status === 'D') return false;

            // Have to be associated with this entity type (handled now through getMappingFor('entityType')
            // if (q.entityType !== entity.entityType) return false;

            // If this applies to all case types, return it
            if (!q.appliesTo || q.appliesTo.length === 0) return true;

            // If this entity's case type(s) match any of the red flag type's case types (including generic categories)
            else {
                const allCaseTypes = store.getState().caseTypes;
                if (entity instanceof Lead) return CaseHelper.questionAppliesToAnyCaseType(q, entity.caseTypes, allCaseTypes);
                // A matter with no case type has no applicable case-type IDs; pass
                // [] (not [null]) so questionAppliesToAnyCaseType's empty-set guard
                // returns false instead of mis-handling a null id (OWA-554).
                else return CaseHelper.questionAppliesToAnyCaseType(q, entity.caseTypeId ? [entity.caseTypeId] : [], allCaseTypes);
            }
        })
        .sort((a, b) => a.position - b.position);

    return util.sortEntityArrayByString<RedFlagType>(redFlagTypes, "name");
}

/**
 * OWA-473: the red-flag types to DISPLAY for an entity.
 *
 * A checked red flag is a durable fact about the client ("Client has insane
 * expectations") and must stay visible even after the case type changes to one
 * where its type no longer applies. So the display set is the union of:
 *   - the types applicable to the entity's CURRENT case type (the offered,
 *     unchecked options — see getRedFlagTypes), and
 *   - every type that already has a CHECKED flag on this entity, regardless of
 *     whether it currently applies.
 *
 * The case type therefore gates which UNCHECKED flags are offered; it never
 * hides a CHECKED one. If a checked flag's type was hard-deleted, it is surfaced
 * via a minimal synthetic type so the flag still renders.
 *
 * @param {Lead | Matter} target
 * @returns {RedFlagType[]}
 */
export function getDisplayRedFlagTypes(target: Lead | Matter): RedFlagType[] {
    return computeDisplayRedFlagTypes(
        getRedFlagTypes(target),
        getRedFlags(target.id),
        id => store.getState().redFlagTypes.get(id),
    );
}

/**
 * Pure core of getDisplayRedFlagTypes (no store access) so the union/orphan rules
 * are unit-testable. Returns the applicable types, then appends one entry for each
 * type that has a checked flag but isn't already applicable.
 *
 * @param {RedFlagType[]} applicable - types applicable to the current case type
 * @param {RedFlag[]} flags - all red flags on the entity
 * @param {(id: uuid) => RedFlagType | undefined} lookupType - resolve a type by id
 * @returns {RedFlagType[]}
 */
export function computeDisplayRedFlagTypes(
    applicable: RedFlagType[],
    flags: RedFlag[],
    lookupType: (id: uuid) => RedFlagType | undefined,
): RedFlagType[] {
    const shownIds = new Set(applicable.map(t => t.id));

    const extra: RedFlagType[] = [];
    flags
        .filter(rf => rf.checked && rf.status !== 'D')
        .forEach(rf => {
            if (shownIds.has(rf.redFlagTypeId)) return;
            shownIds.add(rf.redFlagTypeId);
            // Use the real type (even if soft-deleted, so its name/weight survive);
            // synthesize a fallback only when it is truly gone.
            extra.push(lookupType(rf.redFlagTypeId) ?? orphanRedFlagType(rf.redFlagTypeId));
        });

    return [...applicable, ...extra];
}

/**
 * Minimal stand-in RedFlagType for a checked flag whose type was hard-deleted,
 * so the flag still renders instead of vanishing. Weight 100 to match the risk
 * default for an unresolvable type (RiskHelper/getRisk use `type?.weight ?? 100`),
 * so the displayed weight and the risk score agree; sorts last.
 *
 * @param {uuid} redFlagTypeId
 * @returns {RedFlagType}
 */
function orphanRedFlagType(redFlagTypeId: uuid): RedFlagType {
    return new RedFlagType({
        redFlagTypeId,
        name: '(retired red flag)',
        weight: 100,
        position: Number.MAX_SAFE_INTEGER,
        status: 'C',
    });
}
